import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShieldCheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import ConfirmationModal from './ConfirmationModal';

export default function VerifyCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [confirmModal, setConfirmModal] = useState<any>(null);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  // Timer for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newCode.every(digit => digit !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];

    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }

    setCode(newCode);

    // Focus last filled input or next empty one
    const lastFilledIndex = Math.min(pastedData.length - 1, 5);
    inputRefs.current[lastFilledIndex]?.focus();

    // Auto-submit if complete
    if (newCode.every(digit => digit !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleSubmit = async (verificationCode?: string) => {
    const codeToVerify = verificationCode || code.join('');

    if (codeToVerify.length !== 6) {
      setError('Bitte geben Sie den vollständigen 6-stelligen Code ein.');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await axios.post(`${BASE_URL}/api/auth/verify-code`, {
        email,
        code: codeToVerify
      });

      if (response.data.success) {
        setConfirmModal({
          visible: true,
          type: 'success',
          title: 'Verifizierung erfolgreich!',
          message: 'Ihr Account wurde erfolgreich aktiviert. Sie können sich jetzt anmelden.',
          variant: 'single',
          button: {
            text: 'Zum Login',
            onPress: () => {
              navigate('/login', { state: { email } });
            }
          }
        });
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      setError(error.response?.data?.message || 'Ungültiger oder abgelaufener Code.');

      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    try {
      const response = await axios.post(`${BASE_URL}/api/auth/resend-code`, {
        email
      });

      if (response.data.success) {
        setConfirmModal({
          visible: true,
          type: 'info',
          title: 'Code erneut gesendet',
          message: `Ein neuer Verifizierungscode wurde an ${email} gesendet.`,
          variant: 'single',
          button: {
            text: 'OK',
            onPress: () => setConfirmModal(null)
          }
        });

        // Reset timer
        setResendTimer(60);
        setCanResend(false);
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error: any) {
      console.error('Resend error:', error);
      setError(error.response?.data?.message || 'Fehler beim erneuten Senden des Codes.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
          {/* Icon and Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
              <ShieldCheckIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              E-Mail verifizieren
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Wir haben einen 6-stelligen Code an
            </p>
            <p className="font-medium text-gray-900 dark:text-white">
              {email}
            </p>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              gesendet. Bitte geben Sie den Code ein.
            </p>
          </div>

          {/* Code Input Fields */}
          <div className="flex justify-center gap-2 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition"
                disabled={isVerifying}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-error-100 dark:bg-error-900/30 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-300 rounded-lg text-sm text-center">
              {error}
            </div>
          )}

          {/* Verify Button */}
          <button
            onClick={() => handleSubmit()}
            disabled={isVerifying || code.some(digit => digit === '')}
            className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          >
            {isVerifying ? 'Wird verifiziert...' : 'Code verifizieren'}
          </button>

          {/* Resend Code */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Code nicht erhalten?
            </p>
            <button
              onClick={handleResendCode}
              disabled={!canResend || isVerifying}
              className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <ArrowPathIcon className="h-4 w-4" />
              {canResend ? 'Code erneut senden' : `Erneut senden in ${resendTimer}s`}
            </button>
          </div>

          {/* Back to Register */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => navigate('/register')}
              className="w-full text-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition"
            >
              Zurück zur Registrierung
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <ConfirmationModal
          visible={confirmModal.visible}
          onClose={() => setConfirmModal(null)}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          button={confirmModal.button}
          buttons={confirmModal.buttons}
        />
      )}
    </div>
  );
}